import { useDispatch } from "react-redux";

import { EvaChart } from "constants/charts.enums";
import { ChartStatus, useChartsContext } from "contexts/ChartContext";
import { UNDEFINED_LOCKS } from "entities/charts/chart";
import { updateChart } from "store/features";
import styled from "styled-components";

import { useChartEntities } from "hooks/charts";

import { BaseMenu } from "components/base";
import Icon_RigFracActivityVsTime from "components/icons/Icon_RigFracActivityVsTime";
import Icon_RigFracRecentActivity from "components/icons/Icon_RigFracRecentActivity";

import { WELL_STATUS_CHART_TYPES } from "../../../constants";
import {
  ChartTypeButton,
  ChartTypeMenuContainer
} from "../components/ChartSelector/ChartSelector.styles";
import ChartTypeHeader from "../components/ChartTypeHeader";

/**
 * Icon per Well Status chart type. Reuses the rig/frac icons: the two widgets have
 * the same pair of shapes (bars over time, ranked horizontal bars), so a bespoke
 * pair would be visually identical artwork under different names. Swap these for
 * dedicated icons if design asks.
 */
function WellStatusChartIcon({ chartType }: { chartType: EvaChart }) {
  return chartType === EvaChart.WellStatusCurrent ? (
    <Icon_RigFracRecentActivity />
  ) : (
    <Icon_RigFracActivityVsTime />
  );
}

/**
 * Scoped chart selector for the Well Status widget. Renders the
 * WELL_STATUS_CHART_TYPES group only (Well Status vs Time + Current Well Status).
 * A local sibling of ChartSelector, which is wells-bound (session restore, product
 * defaults, positional icon arrays).
 */
function WellStatusChartSelector() {
  const dispatch = useDispatch();
  const { id, setStatus } = useChartsContext();
  const { chartType } = useChartEntities(id);

  const handleSelect = (nextChartType: EvaChart, closeMenu: () => void) => {
    closeMenu();
    if (nextChartType === chartType) return;

    setStatus(ChartStatus.Loading);
    dispatch(
      updateChart({
        id,
        chartType: nextChartType,
        // `features` is deliberately NOT rebuilt. Both Well Status chart types
        // resolve to the SAME feature set (see the features factory), so
        // createFeatureEntities would return an identical shape while resetting
        // every `active` flag back to its default — silently switching the
        // legend back on and the chart focus back off on each switch, discarding
        // what the user had chosen. A test pins the shared-feature-set premise.
        locks: UNDEFINED_LOCKS,
        // Drop the previous chart's response so the new chart never renders the
        // old shape (the two have different series geometry).
        response: undefined
      })
    );
  };

  return (
    <div className="charts-selector flex">
      <BaseMenu
        trigger={
          <TriggerWrapper
            aria-label="Select Chart Type"
            data-testid="well-status-chart-type-selector">
            <WellStatusChartIcon chartType={chartType as EvaChart} />
          </TriggerWrapper>
        }>
        {({ closeMenu }) => (
          <ChartTypeMenuContainer>
            {WELL_STATUS_CHART_TYPES.map((header) => (
              <ChartTypeHeader key={header.name} header={header.name}>
                {header.items.map((ct) => (
                  <ChartTypeButton
                    key={ct}
                    className={ct === chartType ? "isSelected" : "isAvailable"}
                    data-testid={`well-status-chart-type-${ct}`}
                    onClick={() => handleSelect(ct as EvaChart, closeMenu)}>
                    <WellStatusChartIcon chartType={ct as EvaChart} />
                    <span>{ct}</span>
                  </ChartTypeButton>
                ))}
              </ChartTypeHeader>
            ))}
          </ChartTypeMenuContainer>
        )}
      </BaseMenu>
    </div>
  );
}

export default WellStatusChartSelector;

const TriggerWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
  cursor: pointer;

  svg {
    color: #a2aaad;
    transition: color var(--duration-short);
  }

  &:hover svg {
    color: var(--color-primary);
  }
`;
